// Pacing + chunking for the v2 wire-format animation queue.
//
// One engine action can resolve through many phases of game state —
// declare attackers, declare blockers, deal damage, end turn, the
// opponent's whole turn, our draw, our next main — and the v2 wire
// returns the whole sequence in a single events array. Replaying the
// array as one motion frame collapses all of that into one ~380 ms
// layout transition: turns blur past, damage events stack on top of
// each other, and the combat lunge fires while half the attackers
// haven't even been declared yet. v1 paced this by walking
// per-snapshot Steps; v2 has to chunk the event stream itself.
//
// We can't chunk on `phase` / `turn` events — the translator emits at
// most one of each per batch, carrying the FINAL turn / phase, not
// every intermediate transition. The actual per-beat structure lives
// in entity events: each Move (a play, a draw, a death moving to
// graveyard), each AttackersDeclared, each Block, each contiguous run
// of Damage events. `splitEventsIntoFrames` cuts after those so each
// chunk is one user-visible beat.

import type { WireEvent } from "../api/types";

// 300 ms is the smallest value the user reported as comfortable: lower
// and consecutive phase ticks read as a flash; higher stacks dead air
// on top of every already-paced transition.
export const MIN_STEP_DELAY_MS = 300;

/** A Move that represents a creature leaving the battlefield for the
 *  graveyard — i.e. paired with a Die event for the same eid. Treated
 *  as a death-half rather than a generic zone change so the splitter
 *  can group simultaneous deaths into one animation beat. */
function isDeathMove(ev: WireEvent): boolean {
  return ev.k === "move" && ev.from === "battle" && ev.to === "graveyard";
}

/** True if `ev` is one half of a death (the Die fact or the matching
 *  battle→graveyard Move). Deaths cluster: combat resolves several at
 *  once, "destroy every other creature" resolves the whole board at
 *  once, etc. — animating them one at a time stretches a single beat
 *  into seconds. */
function isDeathHalf(ev: WireEvent): boolean {
  return ev.k === "die" || isDeathMove(ev);
}

/** True if `ev` is the start-of-cast move (hand → stack) — paired
 *  with a stack → destination resolve later in the same batch. The
 *  splitter keeps both halves in one chunk so Motion's layoutId
 *  carries the play-clone rect to the new zone slot in one beat.
 *  Splitting them stranded the cast as a fade-out at the release
 *  point + fade-in on the board. */
function isCastStartMove(ev: WireEvent): boolean {
  return ev.k === "move" && ev.from === "hand" && ev.to === "stack";
}

/** True if `ev` ends an atomic user-visible beat — the chunk
 *  containing it should end after it so the next beat gets its own
 *  view transition. Non-death Moves (plays into battle, draws, returns
 *  to hand) end chunks; the death-half Moves are handled by the death
 *  grouping in the splitter. The cast-start half (hand → stack) does
 *  NOT end its chunk — the chunk runs through to the matching
 *  stack → destination resolve so the VT pairs OLD/NEW across the
 *  whole cast. Damage and death events also get special-cased there
 *  (consecutive runs collapse into one chunk so combat / sweep effects
 *  animate as one beat). Phase events are visible beats too — the
 *  engine ships one per phase transition so the SPA can animate banner
 *  transitions like Main → CombatStart → DeclareAttackers with the
 *  pacing floor between each instead of cramming the whole cascade
 *  into one frame. The Trap-sprung event is its own beat so the
 *  trap's flip / fizzle reads on its own. */
function endsChunk(ev: WireEvent): boolean {
  if (ev.k === "move") return !isDeathMove(ev) && !isCastStartMove(ev);
  return (
    ev.k === "attackers_declared" ||
    ev.k === "block" ||
    ev.k === "phase" ||
    ev.k === "trap_sprung"
  );
}

/** Events that carry no user-visible beat of their own — pure scalar
 *  diffs (mana, hand_size, …), the per-eid `castable` patch the
 *  reducer feeds the hand grey-out, the trailing `pending_changed`
 *  the picker keys off, and the final phase / turn deltas the wire
 *  collapses to one event at the end of a batch. Anything in this set
 *  riding the tail of a batch should land in the SAME chunk as the
 *  prior entity event so its consequences (mana dropping, cards going
 *  grey, the next picker opening) appear in the same animation beat
 *  as the cause — not 300 ms later in a separately-paced "tail" chunk. */
function isTailScalar(ev: WireEvent): boolean {
  switch (ev.k) {
    case "life":
    case "mana":
    case "max_mana":
    case "hand_size":
    case "deck_size":
    case "graveyard_size":
    case "exile_size":
    case "castable":
    case "pending_changed":
    case "turn":
    case "game_over":
    case "attr":
    case "keyword_add":
    case "keyword_remove":
    case "counter":
      return true;
    // Note: `phase` is intentionally NOT a tail scalar — phase
    // events are visible beats (see `endsChunk`) and need to pace
    // independently, not fold into a prior chunk's view transition.
    default:
      return false;
  }
}

/** True if the chunk must hold the inter-step pacing floor even when
 *  it produces no visible snapshot diff. Phase-only chunks apply
 *  instantly (nothing on the board moves — the phase chip carries its
 *  own CSS transition), but they still need the floor: without it the
 *  Main → CombatStart → DeclareAttackers → … cascade of an opponent's
 *  turn flashes past in a single frame, which is exactly what
 *  `endsChunk` splits phase events out to prevent. */
export function needsPacingFloor(
  frame: WireEvent[],
  visible: boolean,
): boolean {
  return visible || frame.some((ev) => ev.k === "phase");
}

/** Split a wire-event batch into per-beat chunks. Each chunk ends at
 *  a non-death Move / AttackersDeclared / Block, at the tail of a run
 *  of contiguous Damage events, or at the tail of a run of contiguous
 *  death events (Die + matching battle→graveyard Move, in any
 *  interleaving). Leading Reveal / Phase / Turn events that precede
 *  an entity event stay with the chunk they belong to. Trailing
 *  scalar diffs and pending_changed / game_over fold INTO the prior
 *  chunk rather than forming a separate paced chunk — they're the
 *  consequences of the prior beat (mana spent, hand size dropped, the
 *  next picker opened) and must land in the same view transition as
 *  the cause so the user sees cards grey out, mana counters tick down,
 *  and the next picker open simultaneously with the play / damage
 *  step that triggered them. Empty input yields `[]`. */
export function splitEventsIntoFrames(events: WireEvent[]): WireEvent[][] {
  if (events.length === 0) return [];
  const frames: WireEvent[][] = [];
  let cur: WireEvent[] = [];
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    cur.push(ev);
    if (endsChunk(ev)) {
      frames.push(cur);
      cur = [];
      continue;
    }
    if (ev.k === "damage" || isDeathHalf(ev)) {
      // Group consecutive Damage events plus their trailing death
      // halves into one chunk. Combat damage is simultaneous, and the
      // creatures that die from it die in the same instant — splitting
      // damage from the matching deaths stretches a single beat into
      // "HP drops" → wait → "creature disappears." The death-half run
      // tails the damage run; once we hit anything that isn't a damage
      // or death event the chunk ends.
      const next = events[i + 1];
      const nextIsCombatResult =
        next && (next.k === "damage" || isDeathHalf(next));
      if (!nextIsCombatResult) {
        frames.push(cur);
        cur = [];
      }
    }
  }
  // Tail merge: if the leftover bucket is nothing but scalar /
  // pending / game_over events (no entity events of its own), it
  // describes the AFTERMATH of the last beat — mana counters,
  // grey-out flips, the next pending decision opening — and must
  // land in the same view transition as the beat that caused it.
  // If it contains its own entity events (rare; would mean an event
  // type endsChunk doesn't recognise) it stays as a final frame so
  // we don't silently drop animation work.
  if (cur.length > 0) {
    const tailIsScalarOnly = cur.every(isTailScalar);
    if (tailIsScalarOnly && frames.length > 0) {
      frames[frames.length - 1] = frames[frames.length - 1].concat(cur);
    } else {
      frames.push(cur);
    }
  }
  return frames;
}
